// =============================================================================
// DAMS — ISSP Analytics Engine · Iloilo City Government
// =============================================================================

import type {
  Computer, ComputerSoftware, CitySystem,
  NetworkSurveyItem, SecuritySurveyItem, Department,
  EolMetrics, LicenseAudit, OpexByDept,
  CybersecurityScore, CyberIndicator, IsspDashboardMetrics,
  Printer, MobileDevice, OtherEquipment, NetworkDevice,
  OsGroup, AgeGroup, DeptAssetSummary,
  RefreshPriorityItem, OpexByCluster, Win11UpgradeFunnel,
} from './types';

// TPM 2.0 became standard on OEM desktops/laptops around this year — used as a
// proxy for Win11 hardware eligibility since the inventory has no CPU/RAM specs.
const WIN11_ELIGIBLE_YEAR = 2018;

export const CURRENT_YEAR        = new Date().getFullYear();
export const EOL_THRESHOLD_YEARS = 5;
export const UNIT_REFRESH_COST   = 45_000; // PHP per desktop/laptop

// ---------------------------------------------------------------------------
// Department Registry
// ---------------------------------------------------------------------------
export const DEPARTMENT_MAP: Record<number, Department> = {
  65: { dept_id: 65, name: "OSCA", acronym: "OSCA", cluster: "Social" },
  67: { dept_id: 67, name: "PDAO", acronym: "PDAO", cluster: "Social" },
  68: { dept_id: 68, name: "LEDIP", acronym: "LEDIP", cluster: "Economic" },
  69: { dept_id: 69, name: "BPLO", acronym: "BPLO", cluster: "Revenue" },
  70: { dept_id: 70, name: "CTO", acronym: "CTO", cluster: "Finance" },
  71: { dept_id: 71, name: "Office of the City Assessor", acronym: "ASSESSOR", cluster: "Revenue" },
  72: { dept_id: 72, name: "CHO", acronym: "CHO", cluster: "Health" },
  73: { dept_id: 73, name: "GSO - Admin", acronym: "GSO", cluster: "Administrative" },
  74: { dept_id: 74, name: "GSO - Solid Waste Management", acronym: "GSO", cluster: "Environment" },
  75: { dept_id: 75, name: "PIPSMO", acronym: "PIPSMO", cluster: "ICT" },
  76: { dept_id: 76, name: "Barangay Affairs", acronym: "BA", cluster: "Social" },
  77: { dept_id: 77, name: "Office of the Zoning Administrator", acronym: "ZA", cluster: "Planning" },
  78: { dept_id: 78, name: "City Legal Office", acronym: "LEGAL", cluster: "Executive" },
  79: { dept_id: 79, name: "CMO Task Force Boltahe", acronym: "CTFB", cluster: "Executive" },
  80: { dept_id: 80, name: "CMO Beautification Program", acronym: "CBP", cluster: "Executive" },
  81: { dept_id: 81, name: "Office of the City Planning and Development Coordinator", acronym: "PDC", cluster: "Planning" },
  82: { dept_id: 82, name: "City Engineers Office", acronym: "ENGINEER", cluster: "Infrastructure" },
  83: { dept_id: 83, name: "CENRO", acronym: "CENRO", cluster: "Environment" },
  84: { dept_id: 84, name: "City Budget Office", acronym: "BUDGET", cluster: "Finance" },
  85: { dept_id: 85, name: "City Civil Registrars' Office", acronym: "CR", cluster: "Administrative" },
  86: { dept_id: 86, name: "MICE", acronym: "MICE", cluster: "Economic" },
  87: { dept_id: 87, name: "Sports Development Division", acronym: "SDD", cluster: "Social" },
  88: { dept_id: 88, name: "REACH", acronym: "REACH", cluster: "Economic" },
  89: { dept_id: 89, name: "ICAST", acronym: "ICAST", cluster: "Economic" },
  90: { dept_id: 90, name: "PESO", acronym: "PESO", cluster: "Employment" },
  91: { dept_id: 91, name: "PIO", acronym: "PIO", cluster: "Executive" },
  92: { dept_id: 92, name: "City Population Office", acronym: "POPULATI", cluster: "Social" },
  93: { dept_id: 93, name: "ICLHO", acronym: "ICLHO", cluster: "Health" },
  94: { dept_id: 94, name: "IASO", acronym: "IASO", cluster: "General" },
  95: { dept_id: 95, name: "Community Development Division (Barangay Secretariat)", acronym: "CDDBS", cluster: "Social" },
  96: { dept_id: 96, name: "CMO - Public-Private Partnership Selection Committee", acronym: "CMO", cluster: "Executive" },
  97: { dept_id: 97, name: "TTMO", acronym: "TTMO", cluster: "Economic" },
  98: { dept_id: 98, name: "POSMO", acronym: "POSMO", cluster: "Economic" },
  99: { dept_id: 99, name: "POSMO - BFP", acronym: "POSMO", cluster: "Safety" },
  100: { dept_id: 100, name: "POSMO - ASISU", acronym: "POSMO", cluster: "Economic" },
  101: { dept_id: 101, name: "POSMO - SSD", acronym: "POSMO", cluster: "Economic" },
  102: { dept_id: 102, name: "Vice Mayor's Office", acronym: "VM", cluster: "Legislative" },
  103: { dept_id: 103, name: "Sangguniang Panlungsod Admin", acronym: "SPA", cluster: "Legislative" },
  104: { dept_id: 104, name: "SP - SESSION HALL", acronym: "SP", cluster: "Legislative" },
  105: { dept_id: 105, name: "SP - Secretariat", acronym: "SP", cluster: "Legislative" },
  106: { dept_id: 106, name: "SP - Archives", acronym: "SP", cluster: "Legislative" },
  107: { dept_id: 107, name: "City Accountant's Office", acronym: "ACCOUNTA", cluster: "Finance" },
  108: { dept_id: 108, name: "Councilor Ganzon's Office", acronym: "CG", cluster: "Legislative" },
  109: { dept_id: 109, name: "Councilor Estante's Office", acronym: "CE", cluster: "Legislative" },
  110: { dept_id: 110, name: "Councilor Nava's Office", acronym: "CN", cluster: "Legislative" },
  111: { dept_id: 111, name: "Councilor Young's Office", acronym: "CY", cluster: "Legislative" },
  112: { dept_id: 112, name: "Councilor Baronda's Office", acronym: "CB", cluster: "Legislative" },
  113: { dept_id: 113, name: "Councilor Duron's Office", acronym: "CD", cluster: "Legislative" },
  114: { dept_id: 114, name: "Councilor Zaldivar's Office", acronym: "CZ", cluster: "Legislative" },
  115: { dept_id: 115, name: "Councilor Parcon's Office", acronym: "CP", cluster: "Legislative" },
  116: { dept_id: 116, name: "Councilor Tupas' Office", acronym: "CT", cluster: "Legislative" },
  117: { dept_id: 117, name: "Councilor Impica's Office", acronym: "CI", cluster: "Legislative" },
  118: { dept_id: 118, name: "Councilor Cabaluna's Office", acronym: "CC", cluster: "Legislative" },
  119: { dept_id: 119, name: "Councilor Ong's Office", acronym: "CO", cluster: "Legislative" },
  120: { dept_id: 120, name: "Councilor Trena's Office", acronym: "CT", cluster: "Legislative" },
  121: { dept_id: 121, name: "Councilor Sarabia's Office", acronym: "CS", cluster: "Legislative" },
  122: { dept_id: 122, name: "CMO - Administrative Division", acronym: "CMO", cluster: "Executive" },
  123: { dept_id: 123, name: "CMO - City Mayor's Office", acronym: "CMO", cluster: "Executive" },
  124: { dept_id: 124, name: "CMO - Office of the Executive Assistant", acronym: "CMO", cluster: "Executive" },
  125: { dept_id: 125, name: "CMO - Office of the Senior Executive", acronym: "CMO", cluster: "Executive" },
  126: { dept_id: 126, name: "CMO - Accountable Officer - Mathilde Trenas", acronym: "CMO", cluster: "Executive" },
  127: { dept_id: 127, name: "CMO - Social Media Team", acronym: "CMO", cluster: "Executive" },
  128: { dept_id: 128, name: "CMO - Special Asst to the Mayor", acronym: "CMO", cluster: "Executive" },
  129: { dept_id: 129, name: "CMO - Uswag Scholar Office", acronym: "CMO", cluster: "Health" },
  130: { dept_id: 130, name: "Office of the City Administrator", acronym: "ADMINIST", cluster: "Executive" },
  131: { dept_id: 131, name: "BAC", acronym: "BAC", cluster: "General" },
  132: { dept_id: 132, name: "CHRMO", acronym: "CHRMO", cluster: "Administrative" },
  133: { dept_id: 133, name: "CHRMO - Admin", acronym: "CHRMO", cluster: "Administrative" },
  134: { dept_id: 134, name: "Office of the Building Administrator", acronym: "BA", cluster: "Infrastructure" },
  135: { dept_id: 135, name: "CMO - PPEID", acronym: "CMO", cluster: "Executive" },
  136: { dept_id: 136, name: "DASMO - Admin", acronym: "DASMO", cluster: "ICT" },
  137: { dept_id: 137, name: "DASMO - ICT", acronym: "DASMO", cluster: "ICT" },
  138: { dept_id: 138, name: "DASMO - CBMS", acronym: "DASMO", cluster: "ICT" },
  139: { dept_id: 139, name: "DASMO - Server Room", acronym: "DASMO", cluster: "ICT" },
  140: { dept_id: 140, name: "CMO - LGBTQ Affairs", acronym: "CMO", cluster: "Social" },
  141: { dept_id: 141, name: "Office of the City Tourism and Development Officer", acronym: "TDO", cluster: "Economic" },
  142: { dept_id: 142, name: "Office of the City Agriculturist", acronym: "AGRICULT", cluster: "Economic" },
  143: { dept_id: 143, name: "Office of the Building Official", acronym: "BO", cluster: "Infrastructure" },
  144: { dept_id: 144, name: "CEO - Warehouse/Motorpool", acronym: "CEO", cluster: "Infrastructure" },
  145: { dept_id: 145, name: "Office of the City Veterinarian", acronym: "VETERINA", cluster: "Health" },
  146: { dept_id: 146, name: "BPLD - Mary Mart Mall eBOSS Off-site Center", acronym: "BPLD", cluster: "Revenue" },
  147: { dept_id: 147, name: "BPLD - The Atrium eBOSS Off-site Center", acronym: "BPLD", cluster: "Revenue" },
  150: { dept_id: 150, name: "GSO - SOLID WASTE CALAJUNAN", acronym: "GSO", cluster: "Environment" },
  151: { dept_id: 151, name: "GSO - MOLO WAREHOUSE", acronym: "GSO", cluster: "Administrative" },
  152: { dept_id: 152, name: "GSO - IEC - SWM FORT SAN PEDRO", acronym: "GSO", cluster: "Administrative" },
  153: { dept_id: 153, name: "BPLD - Festive Mall eBOSS Off-site Center", acronym: "BPLD", cluster: "Revenue" },
  154: { dept_id: 154, name: "CHO - DISTRICT HEALTH CENTER (TANZA)", acronym: "CHO", cluster: "Health" },
  155: { dept_id: 155, name: "CHO - ILOILO CITY SOCIAL HYGIENE CLINIC (TANZA)", acronym: "CHO", cluster: "Health" },
  156: { dept_id: 156, name: "CHO - MATERNITY AND REPRODUCTIVE HEALTH CENTER LAPAZ", acronym: "CHO", cluster: "Health" },
  157: { dept_id: 157, name: "CHO - DISTRICT HEALTH CENTER (LAPAZ)", acronym: "CHO", cluster: "Health" },
  159: { dept_id: 159, name: "CHO - NUTRITION CENTER", acronym: "CHO", cluster: "Health" },
  160: { dept_id: 160, name: "CHO - DISTRICT HEALTH CENTER (SANTO ROSARIO, SAN PEDRO)", acronym: "CHO", cluster: "Health" },
  161: { dept_id: 161, name: "CHO - USWAG MOLECULAR LABORATORY (ICCC, SAN PEDRO)", acronym: "CHO", cluster: "Health" },
  162: { dept_id: 162, name: "CHO - DISTRICT HEALTH CENTER (BO. OBRERO)", acronym: "CHO", cluster: "Health" },
  163: { dept_id: 163, name: "BPLD - SM CITY ILOILO eBOSS Off-site Center", acronym: "BPLD", cluster: "Revenue" },
  164: { dept_id: 164, name: "CHO - DISTRICT HEALTH CENTER (MANDURRIAO)", acronym: "CHO", cluster: "Health" },
  165: { dept_id: 165, name: "CHO DISTRICT HEALTH CENTER JARO 2", acronym: "CDHCJ", cluster: "Health" },
  169: { dept_id: 169, name: "CHO - DISTRICT HEALTH CENTER (JARO 1)", acronym: "CHO", cluster: "Health" },
  170: { dept_id: 170, name: "CHO - SAN ISIDRO DIALYSIS CENTER", acronym: "CHO", cluster: "Health" },
  171: { dept_id: 171, name: "CHO - DISTRICT HEALTH CENTER (MOLO)", acronym: "CHO", cluster: "Health" },
  172: { dept_id: 172, name: "CHO - STROKE REHABILITATION CENTER", acronym: "CHO", cluster: "Health" },
  173: { dept_id: 173, name: "CHO - USWAG DIALYSIS CENTER (EAST BALUARTE, MOLO)", acronym: "CHO", cluster: "Health" },
  174: { dept_id: 174, name: "CHO - SUPPLY CHAIN MANAGEMENT UNIT", acronym: "CHO", cluster: "Health" },
  175: { dept_id: 175, name: "CHO - AREVALO DISTRICT HEALTH CENTER", acronym: "CHO", cluster: "Health" },
  176: { dept_id: 176, name: "CHO - CALUMPANG LYING IN", acronym: "CHO", cluster: "Health" },
  177: { dept_id: 177, name: "CDRMMO - ICARE CENTER (GAISANO)", acronym: "CDRMMO", cluster: "Safety" },
  179: { dept_id: 179, name: "CDRMMO - ICARE CENTER (BRGY CALUMPANG, MOLO)", acronym: "CDRMMO", cluster: "Safety" },
  180: { dept_id: 180, name: "CDRRMO - MOTORPOOL", acronym: "CDRRMO", cluster: "Safety" },
  181: { dept_id: 181, name: "CDRMMO - ICARE CENTER SOOC", acronym: "CDRMMO", cluster: "Safety" },
  182: { dept_id: 182, name: "CDRMMO - I-CARE CENTER (NABITASAN)", acronym: "CDRMMO", cluster: "Safety" },
  183: { dept_id: 183, name: "CDRMMO - I-CARE CENTER (UNGKA)", acronym: "CDRMMO", cluster: "Safety" },
  184: { dept_id: 184, name: "CDRRMO - I-CARE TANZA", acronym: "CDRRMO", cluster: "Safety" },
  188: { dept_id: 188, name: "CDRRMO - I-CARE BALANTANG", acronym: "CDRRMO", cluster: "Safety" },
  189: { dept_id: 189, name: "LEEO - MAIN", acronym: "LEEO", cluster: "Revenue" },
  190: { dept_id: 190, name: "LEEO - CENTRAL MARKET", acronym: "LEEO", cluster: "Revenue" },
  191: { dept_id: 191, name: "LEEO - SLAUGHTERHOUSE", acronym: "LEEO", cluster: "Revenue" },
  192: { dept_id: 192, name: "LEEO - JARO SMALL MARKET", acronym: "LEEO", cluster: "Revenue" },
  193: { dept_id: 193, name: "LEEO - JARO BIG MARKET", acronym: "LEEO", cluster: "Revenue" },
  194: { dept_id: 194, name: "LEEO - LAPAZ DISTRICT", acronym: "LEEO", cluster: "Revenue" },
  195: { dept_id: 195, name: "LEEO - MANDURRIAO MARKET", acronym: "LEEO", cluster: "Revenue" },
  196: { dept_id: 196, name: "CSWDO - MOLO MAIN", acronym: "CSWDO", cluster: "Social" },
  197: { dept_id: 197, name: "CSWDO - CENTER INTERVENTION", acronym: "CSWDO", cluster: "Social" },
  198: { dept_id: 198, name: "CSWDO - ILOILO CITY COUNCIL FOR THE PROTECTION OF CHILDREN", acronym: "CSWDO", cluster: "Social" },
  199: { dept_id: 199, name: "CSWDO - MANDURRIAO", acronym: "CSWDO", cluster: "Social" },
  200: { dept_id: 200, name: "CSWDO - JARO 1", acronym: "CSWDO", cluster: "Social" },
  201: { dept_id: 201, name: "CSWDO - BALAY DALAYUNAN", acronym: "CSWDO", cluster: "Social" },
  202: { dept_id: 202, name: "CSWDO - LAPAZ/LAPUZ DISTRICT", acronym: "CSWDO", cluster: "Social" },
  203: { dept_id: 203, name: "CSWDO - AREVALO", acronym: "CSWDO", cluster: "Social" },
  205: { dept_id: 205, name: "CSWDO - JARO 2", acronym: "CSWDO", cluster: "Social" },
  206: { dept_id: 206, name: "CSWDO - BBY", acronym: "CSWDO", cluster: "Social" },
  207: { dept_id: 207, name: "CSWDO - MOLO DISTRICT", acronym: "CSWDO", cluster: "Social" },
  208: { dept_id: 208, name: "CSWDO - CITY PROPER", acronym: "CSWDO", cluster: "Social" },
  999: { dept_id: 999, name: 'Unknown / Unassigned', acronym: 'N/A', cluster: 'Unclassified' },
};

export function getDepartment(dept_id: number): Department {
  return DEPARTMENT_MAP[dept_id] ?? {
    dept_id,
    name: `Dept Code: ${dept_id}`,
    acronym: `D${dept_id}`,
    cluster: 'Unknown',
  };
}
export const getDeptName    = (id: number) => getDepartment(id).name;
export const getDeptAcronym = (id: number) => getDepartment(id).acronym;

// ---------------------------------------------------------------------------
// 1. Hardware Lifecycle / EOL
// ---------------------------------------------------------------------------
export function computeEolMetrics(computers: Computer[]): EolMetrics {
  const total    = computers.length;
  const eol      = computers.filter(c => c.eol_flag);
  const eolCount = eol.length;
  const eolPct   = total > 0 ? Math.round(eolCount / total * 100) : 0;

  const eolByDept: Record<number, number> = {};
  eol.forEach(c => { eolByDept[c.dept_id] = (eolByDept[c.dept_id] ?? 0) + 1; });

  const proj = {} as EolMetrics['refreshCostProjection'];
  ([10, 50, 100] as const).forEach(pct => {
    proj[pct] = Math.ceil(eolCount * pct / 100) * UNIT_REFRESH_COST;
  });

  return { totalComputers: total, eolCount, eolPercentage: eolPct, eolByDept, refreshCostProjection: proj };
}

// ---------------------------------------------------------------------------
// 2. Software License Integrity (from computer_software table)
// ---------------------------------------------------------------------------
const FLAGGED_LICENSES = ['unlicensed', 'unknown', 'subscription'];

export function computeLicenseAudit(software: ComputerSoftware[]): LicenseAudit {
  let licensed = 0, unlicensed = 0, oem = 0, subscription = 0, freeware = 0, unknown = 0;
  const flagged: ComputerSoftware[] = [];

  software.forEach(s => {
    const l = (s.software_license ?? '').toLowerCase();
    if (l === 'licensed')       licensed++;
    else if (l === 'unlicensed') { unlicensed++; flagged.push(s); }
    else if (l === 'oem')        oem++;
    else if (l === 'subscription') { subscription++; flagged.push(s); }
    else if (l === 'freeware')   freeware++;
    else                         { unknown++; flagged.push(s); }
  });

  return { licensed, unlicensed, oem, subscription, freeware, unknown, flaggedEntries: flagged };
}

// ---------------------------------------------------------------------------
// 3. OPEX TCO from SYSTEMS table
// ---------------------------------------------------------------------------
export function computeOpexByDept(systems: CitySystem[]): OpexByDept[] {
  const map = new Map<number, { cost: number; count: number }>();
  systems.forEach(s => {
    const e = map.get(s.dept_id) ?? { cost: 0, count: 0 };
    map.set(s.dept_id, { cost: e.cost + s.maintenance_cost, count: e.count + 1 });
  });

  return Array.from(map.entries())
    .map(([dept_id, v]) => ({
      dept_id,
      dept_name:    getDeptName(dept_id),
      dept_acronym: getDeptAcronym(dept_id),
      annual_opex:  v.cost,
      system_count: v.count,
    }))
    .sort((a, b) => b.annual_opex - a.annual_opex);
}

// ---------------------------------------------------------------------------
// 3b. OPEX rolled up by department cluster
// ---------------------------------------------------------------------------
export function computeOpexByCluster(opex: OpexByDept[]): OpexByCluster[] {
  const map = new Map<string, { cost: number; depts: Set<number>; systems: number }>();
  opex.forEach(o => {
    const cluster = getDepartment(o.dept_id).cluster;
    const e = map.get(cluster) ?? { cost: 0, depts: new Set<number>(), systems: 0 };
    e.cost += o.annual_opex;
    e.depts.add(o.dept_id);
    e.systems += o.system_count;
    map.set(cluster, e);
  });

  return Array.from(map.entries())
    .map(([cluster, v]) => ({
      cluster,
      annual_opex:  v.cost,
      dept_count:   v.depts.size,
      system_count: v.systems,
    }))
    .sort((a, b) => b.annual_opex - a.annual_opex);
}

// ---------------------------------------------------------------------------
// 4. DICT Cybersecurity Readiness Score
// ---------------------------------------------------------------------------
function isYes(s: string): boolean {
  const l = (s ?? '').toLowerCase().trim();
  return ['yes','true','1','available','active','present','installed','deployed','y'].includes(l);
}

function containsKeyword(text: string, ...keywords: string[]): boolean {
  const l = text.toLowerCase();
  return keywords.some(k => l.includes(k));
}

type SurveyPool = (NetworkSurveyItem | SecuritySurveyItem)[];

interface IndicatorDef {
  label:   string;
  weight:  number;
  source:  'network' | 'security';
  check:   (pool: SurveyPool) => { present: boolean; rawAnswer: string };
}

const INDICATORS: IndicatorDef[] = [
  {
    label: 'Active LAN Connectivity', weight: 15, source: 'network',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'local area network', 'lan'));
      return { present: isYes(f?.answer ?? ''), rawAnswer: f?.answer ?? 'N/A' };
    },
  },
  {
    label: 'Hardware Firewall Deployed', weight: 20, source: 'security',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'security measures'));
      const ans = (f as SecuritySurveyItem)?.details ?? f?.answer ?? '';
      const present = containsKeyword(ans, 'hardware firewall', 'hardware fire');
      return { present, rawAnswer: ans || 'N/A' };
    },
  },
  {
    label: 'UPS / Power Backup', weight: 10, source: 'security',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'security measures'));
      const ans = (f as SecuritySurveyItem)?.details ?? f?.answer ?? '';
      const present = containsKeyword(ans, 'ups', 'back-up power', 'backup power');
      return { present, rawAnswer: ans ? ans.slice(0, 60) + '…' : 'N/A' };
    },
  },
  {
    label: 'Generator / Emergency Power', weight: 10, source: 'security',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'security measures'));
      const ans = (f as SecuritySurveyItem)?.details ?? f?.answer ?? '';
      const present = containsKeyword(ans, 'generator');
      return { present, rawAnswer: ans ? ans.slice(0, 60) + '…' : 'N/A' };
    },
  },
  {
    label: 'Off-site Backup Storage', weight: 20, source: 'security',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'off-site', 'backup', 'recovery'));
      return { present: isYes(f?.answer ?? ''), rawAnswer: f?.answer ?? 'N/A' };
    },
  },
  {
    label: 'GovNet / PhilGovNet Connectivity', weight: 15, source: 'network',
    check: pool => {
      const f = pool.find(i =>
        containsKeyword(i.question, 'internet service provider', 'isp', 'provider')
      );
      const ans = f?.answer ?? '';
      const present = containsKeyword(ans, 'govnet', 'philgovnet', 'gov.ph');
      return { present, rawAnswer: ans || 'N/A' };
    },
  },
  {
    label: 'WAN / Internet Access', weight: 10, source: 'network',
    check: pool => {
      const f = pool.find(i => containsKeyword(i.question, 'wide area network', 'wan'));
      return { present: isYes(f?.answer ?? ''), rawAnswer: f?.answer ?? 'N/A' };
    },
  },
];

function ratingFromPct(pct: number): CybersecurityScore['rating'] {
  if (pct < 30) return 'Critical';
  if (pct < 50) return 'Low';
  if (pct < 70) return 'Moderate';
  if (pct < 90) return 'Good';
  return 'Excellent';
}

export function computeCybersecurityScore(
  networkSurvey: NetworkSurveyItem[],
  securitySurvey: SecuritySurveyItem[],
): CybersecurityScore {
  const maxScore = INDICATORS.reduce((s, i) => s + i.weight, 0);
  let score = 0;

  const indicators: CyberIndicator[] = INDICATORS.map(def => {
    const pool: SurveyPool = def.source === 'network'
      ? networkSurvey : securitySurvey;
    const { present, rawAnswer } = def.check(pool);
    if (present) score += def.weight;
    return { label: def.label, present, weight: def.weight, source: def.source, rawAnswer };
  });

  const pct = maxScore > 0 ? Math.round(score / maxScore * 100) : 0;
  return { score, maxScore, percentage: pct, rating: ratingFromPct(pct), indicators };
}

// ---------------------------------------------------------------------------
// 5. OS Distribution
// ---------------------------------------------------------------------------
function osLabel(os: string): string {
  const l = os.toLowerCase().replace(/[\s\-_.]/g, '');
  if (l === 'win11') return 'Windows 11';
  if (l === 'win10') return 'Windows 10';
  if (l === 'win81' || l === 'win8') return 'Windows 8/8.1';
  if (l === 'win7') return 'Windows 7';
  if (l === 'winxp' || l === 'wxp') return 'Windows XP';
  if (l.startsWith('win')) return `Windows (${os})`;
  if (l.includes('ubuntu') || l.includes('linux') || l.includes('centos') || l.includes('fedora')) return `Linux (${os})`;
  if (l.includes('mac') || l.includes('osx') || l.includes('macos')) return `macOS (${os})`;
  if (l === '' || l === 'unknown' || l === 'n/a') return 'Unknown / Not Set';
  return os;
}

function osIsCurrentWin(os: string): boolean {
  const l = os.toLowerCase().replace(/[\s\-_.]/g, '');
  return l === 'win11';
}

function osIsWindowsEol(os: string): boolean {
  const l = os.toLowerCase().replace(/[\s\-_.]/g, '');
  return l.startsWith('win') && !l.includes('11');
}

export function computeOsDistribution(computers: Computer[]): OsGroup[] {
  const map = new Map<string, number>();
  computers.forEach(c => {
    const key = c.os.toLowerCase().trim() || 'unknown';
    map.set(key, (map.get(key) ?? 0) + 1);
  });
  const total = computers.length;
  return Array.from(map.entries())
    .map(([os, count]) => ({
      os,
      label: osLabel(os),
      count,
      pct: total > 0 ? Math.round(count / total * 100) : 0,
      isEol: osIsWindowsEol(os),
    }))
    .sort((a, b) => b.count - a.count);
}

// ---------------------------------------------------------------------------
// 6. Asset Age Distribution
// ---------------------------------------------------------------------------
export function computeAgeDistribution(computers: Computer[]): AgeGroup[] {
  const now = CURRENT_YEAR;
  return [
    { range: '0-2',  label: '0–2 yrs',  isEol: false, min: 0,   max: 2   },
    { range: '3-5',  label: '3–5 yrs',  isEol: false, min: 3,   max: 5   },
    { range: '6-7',  label: '6–7 yrs',  isEol: true,  min: 6,   max: 7   },
    { range: '8-10', label: '8–10 yrs', isEol: true,  min: 8,   max: 10  },
    { range: '10+',  label: '10+ yrs',  isEol: true,  min: 11,  max: 999 },
    { range: 'N/A',  label: 'No Year',  isEol: false, min: -999, max: -1  },
  ].map(g => ({
    range: g.range,
    label: g.label,
    isEol: g.isEol,
    count: computers.filter(c => {
      if (c.year_acquired === 0) return g.range === 'N/A';
      const age = now - c.year_acquired;
      return age >= g.min && age <= g.max;
    }).length,
  }));
}

// ---------------------------------------------------------------------------
// 5b. Windows 11 Upgrade Funnel
// ---------------------------------------------------------------------------
export function computeWin11UpgradeFunnel(computers: Computer[]): Win11UpgradeFunnel {
  const total    = computers.length;
  const win11    = computers.filter(c => osIsCurrentWin(c.os)).length;
  const nonWin11 = computers.filter(c => !osIsCurrentWin(c.os));
  const upgradeable = nonWin11.filter(c => c.year_acquired >= WIN11_ELIGIBLE_YEAR).length;
  const mustReplace  = nonWin11.length - upgradeable;

  return { total, win11, nonWin11: nonWin11.length, upgradeable, mustReplace };
}

// ---------------------------------------------------------------------------
// 6b. Refresh Priority Queue — EOL computers ranked oldest-first for procurement
// ---------------------------------------------------------------------------
export function computeRefreshPriorityQueue(computers: Computer[]): RefreshPriorityItem[] {
  return computers
    .filter(c => c.eol_flag)
    .map(c => {
      const dept = getDepartment(c.dept_id);
      const age = c.year_acquired > 0 ? CURRENT_YEAR - c.year_acquired : -1;
      return {
        computer_id:   c.computer_id,
        brand_serial:  c.brand_serial,
        os:            c.os,
        year_acquired: c.year_acquired,
        age,
        dept_id:       c.dept_id,
        dept_name:     dept.name,
        dept_acronym:  dept.acronym,
        status:        c.status,
        remarks:       c.remarks,
      };
    })
    .sort((a, b) => b.age - a.age);
}

// ---------------------------------------------------------------------------
// 7. Department Asset Summary
// ---------------------------------------------------------------------------
export function computeDeptAssetSummary(
  computers:  Computer[],
  printers:   Printer[],
  mobile:     MobileDevice[],
  equipment:  OtherEquipment[],
  netDevices: NetworkDevice[],
  systems:    CitySystem[],
  software:   ComputerSoftware[],
): DeptAssetSummary[] {
  const deptIds = new Set<number>([
    ...computers.map(c => c.dept_id),
    ...printers.map(p => p.dept_id),
    ...mobile.map(m => m.dept_id),
    ...equipment.map(e => e.dept_id),
    ...netDevices.map(n => n.dept_id),
    ...systems.map(s => s.dept_id),
  ]);

  const unlicensedByDept: Record<number, number> = {};
  software.forEach(s => {
    const l = (s.software_license ?? '').toLowerCase();
    if (l === 'unlicensed' || l === 'unknown') {
      unlicensedByDept[s.dept_id] = (unlicensedByDept[s.dept_id] ?? 0) + 1;
    }
  });

  const opexByDept: Record<number, number> = {};
  systems.forEach(s => { opexByDept[s.dept_id] = (opexByDept[s.dept_id] ?? 0) + s.maintenance_cost; });

  return Array.from(deptIds)
    .map(dept_id => {
      const dept      = getDepartment(dept_id);
      const deptComps = computers.filter(c => c.dept_id === dept_id);
      const eolC      = deptComps.filter(c => c.eol_flag).length;
      const win11C    = deptComps.filter(c => osIsCurrentWin(c.os)).length;
      const totalAssets =
        deptComps.length +
        printers.filter(p => p.dept_id === dept_id).length +
        mobile.filter(m => m.dept_id === dept_id).length +
        equipment.filter(e => e.dept_id === dept_id).length +
        netDevices.filter(n => n.dept_id === dept_id).length;

      return {
        dept_id,
        dept_name:    dept.name,
        acronym:      dept.acronym,
        cluster:      dept.cluster,
        computers:    deptComps.length,
        printers:     printers.filter(p => p.dept_id === dept_id).length,
        mobile:       mobile.filter(m => m.dept_id === dept_id).length,
        equipment:    equipment.filter(e => e.dept_id === dept_id).length,
        netDevices:   netDevices.filter(n => n.dept_id === dept_id).length,
        systems:      systems.filter(s => s.dept_id === dept_id).length,
        total:        totalAssets,
        eolComputers: eolC,
        eolPct:       deptComps.length > 0 ? Math.round(eolC / deptComps.length * 100) : 0,
        win11Count:   win11C,
        win11Pct:     deptComps.length > 0 ? Math.round(win11C / deptComps.length * 100) : 0,
        unlicensedSw: unlicensedByDept[dept_id] ?? 0,
        totalOpex:    opexByDept[dept_id] ?? 0,
      };
    })
    .filter(d => d.total > 0 || d.systems > 0)
    .sort((a, b) => b.total - a.total);
}

// ---------------------------------------------------------------------------
// Aggregate dashboard metrics
// ---------------------------------------------------------------------------
export function computeIsspMetrics(
  computers:     Computer[],
  software:      ComputerSoftware[],
  printers:      Printer[],
  mobile:        MobileDevice[],
  equipment:     OtherEquipment[],
  netDevices:    NetworkDevice[],
  systems:       CitySystem[],
  networkSurvey: NetworkSurveyItem[],
  securitySurvey:SecuritySurveyItem[],
): IsspDashboardMetrics {
  const eol         = computeEolMetrics(computers);
  const license     = computeLicenseAudit(software);
  const opex        = computeOpexByDept(systems);
  const totalOpex   = opex.reduce((s, o) => s + o.annual_opex, 0);
  const cyber       = computeCybersecurityScore(networkSurvey, securitySurvey);
  const osDist      = computeOsDistribution(computers);
  const ageDist     = computeAgeDistribution(computers);
  const deptSummary = computeDeptAssetSummary(computers, printers, mobile, equipment, netDevices, systems, software);
  const win11Count  = osDist.find(o => o.os === 'win11')?.count ?? 0;
  const refreshQueue  = computeRefreshPriorityQueue(computers);
  const opexByCluster = computeOpexByCluster(opex);
  const win11Funnel   = computeWin11UpgradeFunnel(computers);

  return {
    eol,
    license,
    opex,
    totalOpex,
    cyber,
    refreshQueue,
    opexByCluster,
    win11Funnel,
    totalComputers:  computers.length,
    totalPrinters:   printers.length,
    totalMobile:     mobile.length,
    totalEquipment:  equipment.length,
    totalNetDevices: netDevices.length,
    totalFleet:      computers.length + printers.length + mobile.length + equipment.length + netDevices.length,
    activeComputers: computers.filter(c => c.status === 'Active').length,
    osDistribution:  osDist,
    ageDistribution: ageDist,
    deptSummary,
    win11Count,
    win11Pct:        computers.length > 0 ? Math.round(win11Count / computers.length * 100) : 0,
  };
}
